/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { Icon, type IconName } from '@/components/Icon';
import { InlineLoading } from '@/components/loading/LoadingDots';
import i18n from '@/i18n/i18n';
import { useUiStore } from '@/stores/uiStore';
import { useUserProfile } from '@/api/hooks';
import type { UserProfile } from '@/api/settings';
import {
  borrowed,
  isValidEmail,
  isValidPhone,
  resolve,
  type FieldOptions,
} from '@/lib/profileField';
import { useIsDealerUser, useIsInternalUser } from '@/lib/userRole';
import { useSeededForm } from '@/lib/useSeededForm';
import { vehicleContext } from '@/lib/vehicleLabel';
import { submitFeedbackFlow, type UserFeedbackRequest } from '@/api/feedback';
import { DealershipAddressFields } from './DealershipAddressFields';
import { EditableField, TextField } from './ProfileFields';
import { useDealershipAddress } from './useDealershipAddress';
import { Modal } from './Modal';

/** Feedback categories (icon per category). */
const TYPES: [string, string, IconName][] = [
  ['bug', 'feedback.types.bug', 'warn'],
  ['suggestion', 'feedback.types.suggestion', 'spark'],
  ['question', 'feedback.types.question', 'help'],
  ['data', 'feedback.types.data', 'list'],
  ['performance', 'feedback.types.performance', 'analytics'],
  ['ui', 'feedback.types.ui', 'apps'],
  ['other', 'feedback.types.other', 'chat'],
];

const MIN_CHARS = 20;
const MAX_FILES = 5;

type PartialKind = 'attachments' | 'email' | 'both';

const PARTIAL_COPY: Record<PartialKind, { title: string; detail: string }> = {
  attachments: { title: 'feedback.partialTitle', detail: 'feedback.partialDetail' },
  email: { title: 'feedback.emailPartialTitle', detail: 'feedback.emailPartialDetail' },
  both: { title: 'feedback.bothPartialTitle', detail: 'feedback.bothPartialDetail' },
};

function submitPartialKind(
  attachmentCount: number,
  attachmentsUploaded: boolean | undefined,
  emailSent: boolean | undefined,
): PartialKind | null {
  const attachmentsFailed = attachmentCount > 0 && attachmentsUploaded === false;
  const emailFailed = emailSent === false;
  if (attachmentsFailed && emailFailed) {
    return 'both';
  }
  if (attachmentsFailed) {
    return 'attachments';
  }
  if (emailFailed) {
    return 'email';
  }
  return null;
}

/**
 * Per-attachment ceiling advertised in `feedback.attachmentsHelp`. Keep it at or below
 * spring.servlet.multipart.max-file-size: one oversized part rejects the whole submission.
 */
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** Whether a file is within the per-attachment ceiling. */
function withinSizeLimit(file: File): boolean {
  return file.size <= MAX_FILE_BYTES;
}

/**
 * Today as ISO-8601. Nobody is asked for the date any more, so it is stamped rather than typed, and
 * stamped in the one format that reads the same to whoever picks the report up.
 */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * What the reporter may type. The identity and dealership fields are only here as a fallback for
 * what no system of record answered: whatever is known is shown read-only instead, and corrected in
 * the profile rather than in passing.
 *
 * An alias rather than an interface because useSeededForm asks for Record<string, string>, and only
 * an alias satisfies that: an interface stays open to declaration merging, so TypeScript will not
 * give it the implicit index signature that assignment needs.
 */
type ContactForm = {
  first: string;
  last: string;
  phone: string;
  altPhone: string;
  email: string;
  altEmail: string;
};

/** Which editable fallback backs each identity field, for when nothing has answered for it. */
const IDENTITY: Record<'firstName' | 'lastName' | 'email' | 'phone', keyof ContactForm> = {
  firstName: 'first',
  lastName: 'last',
  email: 'email',
  phone: 'phone',
};

// A slow raster (huge DOM, remote images) must never block the feedback submit; give up after this.
const CAPTURE_TIMEOUT_MS = 15000;

/** Excludes the feedback surface and GPU-backed <canvas> (composited separately) from the raster. */
function ignoreForRaster(el: Element): boolean {
  return (
    el.tagName === 'CANVAS' || el.classList.contains('scrim') || el.classList.contains('fb-modal')
  );
}

/** Composite visible on-page canvases onto the raster (html2canvas cannot read GPU buffers). */
function compositeCanvases(ctx: CanvasRenderingContext2D, scale: number): void {
  for (const src of Array.from(document.querySelectorAll('canvas'))) {
    const rect = src.getBoundingClientRect();
    if (!src.width || !src.height || !rect.width || !rect.height) continue;
    try {
      src.toDataURL();
      ctx.drawImage(
        src,
        rect.left * scale,
        rect.top * scale,
        rect.width * scale,
        rect.height * scale,
      );
    } catch {
      /* tainted canvas - skip to keep the output exportable */
    }
  }
}

/** True when a computed background is unset or fully transparent. */
function isUnsetPageBackground(color: string | undefined): boolean {
  if (!color || color === 'transparent') return true;
  return /^rgba\([^)]*,\s*0\s*\)$/.test(color);
}

/** Resolves a kit role token to a computed background colour, when the engine can. */
function backgroundFromRole(token: string): string | undefined {
  const probe = document.createElement('div');
  probe.style.display = 'none';
  probe.style.backgroundColor = `var(${token})`;
  document.documentElement.appendChild(probe);
  const color = getComputedStyle(probe).backgroundColor;
  probe.remove();
  if (!isUnsetPageBackground(color)) return color;
  return undefined;
}

/** Page fill for html2canvas when getComputedStyle has not resolved one yet. */
function pageBackgroundForRaster(): string {
  const root = getComputedStyle(document.documentElement);
  const resolved = root.backgroundColor;
  if (!isUnsetPageBackground(resolved)) {
    return resolved;
  }
  return backgroundFromRole('--color-background') ?? 'transparent';
}

/** Rasterize the viewport with html2canvas-pro (Tailwind v4 color support). */
async function rasterize(name: string): Promise<File | null> {
  try {
    const html2canvas = (await import('html2canvas-pro')).default;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const bg = pageBackgroundForRaster();
    const canvas = await html2canvas(document.body, {
      scale,
      logging: false,
      useCORS: true,
      backgroundColor: bg,
      width: window.innerWidth,
      height: window.innerHeight,
      x: window.scrollX,
      y: window.scrollY,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      ignoreElements: ignoreForRaster,
    });
    const ctx = canvas.getContext('2d');
    if (ctx) compositeCanvases(ctx, scale);
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ? new File([blob], name, { type: 'image/png' }) : null;
  } catch {
    return null;
  }
}

/** Screenshot of the current viewport, racing a timeout so a hung raster can't block submission. */
async function captureScreenshot(name: string): Promise<File | null> {
  let timer!: ReturnType<typeof setTimeout>;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), CAPTURE_TIMEOUT_MS);
  });
  try {
    const file = await Promise.race([rasterize(name), timeout]);
    // Drop an over-limit raster here so the preview never shows what the submit would reject.
    return file && withinSizeLimit(file) ? file : null;
  } finally {
    clearTimeout(timer);
  }
}

/** Open a File in a new browser tab for previewing; the object URL is released shortly after. */
function viewFile(file: File): void {
  const url = URL.createObjectURL(file);
  window.open(url, '_blank', 'noopener');
  // Give the new tab time to load before releasing the blob.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Gap kept between a dragged FAB and the viewport edge, so it is never pushed out of reach. */
const FAB_MARGIN = 8;

/**
 * Pins the FAB at `left`/`top`, clamped inside the viewport. Only a FAB that has been dragged
 * carries inline coordinates; an undragged one stays anchored bottom-right by its stylesheet.
 */
function placeFab(btn: HTMLElement, left: number, top: number): void {
  const rect = btn.getBoundingClientRect();
  const maxLeft = window.innerWidth - rect.width - FAB_MARGIN;
  const maxTop = window.innerHeight - rect.height - FAB_MARGIN;
  btn.style.left = `${Math.min(Math.max(left, FAB_MARGIN), Math.max(FAB_MARGIN, maxLeft))}px`;
  btn.style.top = `${Math.min(Math.max(top, FAB_MARGIN), Math.max(FAB_MARGIN, maxTop))}px`;
  btn.style.right = 'auto';
  btn.style.bottom = 'auto';
}

/** Draggable feedback FAB + dialog (auto-attaches page context + a live screenshot preview). */
export function FeedbackFab() {
  const { t } = useTranslation();
  const open = useUiStore((s) => s.feedbackOpen);
  const openFeedback = useUiStore((s) => s.openFeedback);
  const closeFeedback = useUiStore((s) => s.closeFeedback);
  const vehicle = useUiStore((s) => s.vehicle);
  const { account } = useAuth();
  const isDealer = useIsDealerUser();
  const isInternal = useIsInternalUser();
  const gmin = account?.gmin ?? '';

  // Dealer-only lookup (same gating as the profile dialog). The profile arrives with GDL already
  // resolved into it, so there is no second address to fetch and nothing here to merge.
  const { data: profile, isLoading: profileLoading } = useUserProfile(gmin, isDealer);

  // The dealership address exactly as the profile dialog understands it. The profile fills itself
  // in from GDL at login, so by the time anyone opens this it is usually already complete: what is
  // shown is then a read-only reminder of what the report will carry, and only the parts nobody has
  // managed to resolve are asked for.
  //
  // Nothing is drawn until the address has settled, so a resolved field never appears as an empty
  // box first. Sending waits on the same thing: a half-resolved address is not one to file.
  const address = useDealershipAddress(profile, isDealer);
  const addressSettled = !isDealer || (!profileLoading && address.ready);
  const identitySettled = !isDealer || !profileLoading;

  const [type, setType] = useState('bug');
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  // Record saved but attachments or the notification email failed.
  const [partialKind, setPartialKind] = useState<PartialKind | null>(null);
  const [shot, setShot] = useState(true);
  const [shotFile, setShotFile] = useState<File | null>(null);
  const [shotUrl, setShotUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const moved = useRef(false);
  const fabRef = useRef<HTMLButtonElement>(null);
  const didCapture = useRef(false);
  // Shared in-flight screenshot capture so submit can await the preview.
  const capturePromise = useRef<Promise<File | null> | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { pathname } = useLocation();

  const trimmed = text.replace(/\s/g, '').length;
  // A dealer's address has to be whole, on the same terms the profile asks for it: a half-filled
  // dealership is worse than none for whoever has to act on the report.
  const canSend = trimmed >= MIN_CHARS && !sending && (!isDealer || address.complete);
  const remaining = MIN_CHARS - trimmed;
  const screen =
    pathname === '/' ? t('feedback.screenHome') : pathname.replace(/^\//, '').replace(/-/g, ' ');
  const pageUrl = window.location.href;
  const shotName = `eDiag-${screen.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${today()}.png`;

  /**
   * Who the reporter is, according to whichever system of record answers for them: the resolved
   * profile for a dealer, the token for everyone else. Presented the way the profile dialog
   * presents it, so a name the account already knows is shown rather than asked for.
   */
  const fromToken: UserProfile = useMemo(() => {
    // split always yields at least one element, so `first` is a string ('' when the name is blank).
    const [first, ...rest] = (account?.name ?? '').trim().split(/\s+/);
    return {
      userId: gmin,
      firstName: first,
      lastName: rest.join(' '),
      email: account?.username ?? '',
      locked: { firstName: 'account', lastName: 'account', email: 'account' },
    };
  }, [account, gmin]);
  const known = isDealer ? profile : fromToken;

  // Seeded rather than initialised: for a dealer the profile lands after the first paint, and a
  // form built from it once would either miss it or overwrite whatever was typed meanwhile.
  const seed: ContactForm = useMemo(
    () => ({
      first: known?.firstName?.trim() ?? '',
      last: known?.lastName?.trim() ?? '',
      email: known?.email?.trim() ?? '',
      phone: known?.phone?.trim() ?? '',
      altPhone: profile?.alternatePhone?.trim() ?? '',
      altEmail: profile?.secondaryEmail?.trim() ?? '',
    }),
    [known, profile],
  );
  const { values: form, change: set, reset: resetForm } = useSeededForm(seed);

  // What is known is shown, not asked for: a name the account answered for is no more the
  // reporter's to retype here than their dealership is. A claim with nothing behind it is no claim,
  // so an account missing a name still leaves the box open.
  const identity = {
    firstName: borrowed(resolve(known, 'firstName', form.first)),
    lastName: borrowed(resolve(known, 'lastName', form.last)),
    email: borrowed(resolve(known, 'email', form.email)),
    phone: borrowed(resolve(known, 'phone', form.phone)),
  };

  // Capture one screenshot preview per open when enabled.
  useEffect(() => {
    if (!open) {
      didCapture.current = false;
      return;
    }
    if (didCapture.current || !shot) return;
    didCapture.current = true;
    let cancelled = false;
    setCapturing(true);
    const pending = captureScreenshot(shotName);
    capturePromise.current = pending;
    void pending.then((file) => {
      if (cancelled) return;
      setCapturing(false);
      if (file) {
        setShotFile(file);
        setShotUrl(URL.createObjectURL(file));
      } else {
        setShot(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, shot, shotName]);

  // Release the object URL when it is replaced or the component unmounts.
  useEffect(
    () => () => {
      if (shotUrl) URL.revokeObjectURL(shotUrl);
    },
    [shotUrl],
  );

  const reset = () => {
    setType('bug');
    setText('');
    resetForm();
    setSent(false);
    setPartialKind(null);
    setShot(true);
    setShotFile(null);
    setShotUrl(null);
    setCapturing(false);
    setFiles([]);
    setDetailsOpen(false);
    setError(null);
    setSending(false);
  };
  const close = () => {
    closeFeedback();
    reset();
  };

  const removeShot = () => {
    setShot(false);
    setShotFile(null);
    setShotUrl(null);
    setCapturing(false);
    capturePromise.current = null;
  };
  const recaptureShot = () => {
    didCapture.current = false;
    setShot(true);
  };
  const removeFile = (index: number) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const send = async () => {
    setSending(true);
    setError(null);
    // Await in-flight capture on submit so the screenshot is not dropped.
    const shotAttachment = shot ? (shotFile ?? (await capturePromise.current)) : null;
    const attachments = [shotAttachment, ...files].filter((f): f is File => f != null);

    /* v8 ignore next -- defensive: type always equals one of the TYPES option values. */
    const typeKey = TYPES.find(([v]) => v === type)?.[1] ?? type;
    const typeLabel = i18n.t(typeKey);
    const blank = (v: string) => v.trim() || undefined;
    const request: UserFeedbackRequest = {
      feedbackForm: {
        date: today(),
        firstName: blank(identity.firstName.value),
        lastName: blank(identity.lastName.value),
        email: blank(identity.email.value),
        // Phone, second point of contact and dealership are dealer-only, matching the profile
        // dialog. The address is sent as resolved, whether it came from the profile or was just
        // filled in here.
        phone: isDealer ? blank(identity.phone.value) : undefined,
        alternatePhone: isDealer ? blank(form.altPhone) : undefined,
        secondaryEmail: isDealer ? blank(form.altEmail) : undefined,
        dealerName: isDealer ? blank(address.values.dealerName) : undefined,
        address: isDealer ? blank(address.values.address) : undefined,
        city: isDealer ? blank(address.values.city) : undefined,
        state: isDealer ? blank(address.values.state) : undefined,
        zipCode: isDealer ? blank(address.values.postalCode) : undefined,
        country: isDealer ? blank(address.values.country) : undefined,
        vin: vehicle?.vin,
        program: isInternal ? vehicle?.program : undefined,
        engineeringRelease: isInternal ? vehicle?.release : undefined,
        urlViewSelection: pageUrl,
        issueDescription: `[${typeLabel}] ${text}`,
      },
      url: pageUrl,
      userId: gmin,
      userName: account?.name ?? '',
      type: isDealer ? 'EXTERNAL' : 'INTERNAL',
    };
    try {
      const result = await submitFeedbackFlow(request, attachments);
      setPartialKind(
        submitPartialKind(attachments.length, result?.attachmentsUploaded, result?.emailSent),
      );
      setSent(true);
    } catch {
      setError(t('feedback.error'));
    } finally {
      setSending(false);
    }
  };

  // A dragged FAB keeps its pixel position, so a resize or a rotation could strand it off-screen.
  // Pull it back inside the new viewport whenever that changes.
  useEffect(() => {
    const reclamp = () => {
      const btn = fabRef.current;
      if (!btn?.style.left) return;
      placeFab(btn, parseFloat(btn.style.left), parseFloat(btn.style.top));
    };
    window.addEventListener('resize', reclamp);
    window.addEventListener('orientationchange', reclamp);
    return () => {
      window.removeEventListener('resize', reclamp);
      window.removeEventListener('orientationchange', reclamp);
    };
  }, []);

  const startDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const startX = e.clientX;
    const startY = e.clientY;
    moved.current = false;
    const onMove = (ev: PointerEvent) => {
      // Drag threshold: distinguish click from incidental pointer jitter.
      if (!moved.current && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 4) return;
      moved.current = true;
      // Clamp drag position so the FAB stays reachable in the viewport.
      placeFab(btn, ev.clientX - offX, ev.clientY - offY);
    };
    // Tear down on pointerup and pointercancel so drag listeners cannot stick.
    const endDrag = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  };

  const descHint =
    text && trimmed < MIN_CHARS
      ? t('feedback.descriptionHintMore', { remaining, suffix: remaining === 1 ? '' : 's' })
      : t('feedback.descriptionHintDefault');

  // Contact fields, drawn with the same controls as the dealership below them. Only the description
  // is required on submit; a malformed number is flagged but never withholds the send button, since
  // a report that cannot be filed is worth less than one with a typo in the phone number.
  const identityField = (label: string, key: keyof typeof identity, opts: FieldOptions = {}) => (
    <TextField
      key={key}
      label={label}
      field={identity[key]}
      onChange={set(IDENTITY[key])}
      {...opts}
    />
  );
  const field = (label: string, key: keyof ContactForm, opts: FieldOptions = {}) => (
    <EditableField key={key} label={label} value={form[key]} onChange={set(key)} {...opts} />
  );
  const phoneHint = (value: string) => (isValidPhone(value) ? null : t('profile.phoneInvalid'));
  const emailHint = (value: string) => (isValidEmail(value) ? null : t('profile.emailInvalid'));

  const ctxRows: [string, string][] = [
    [t('feedback.ctxScreen'), screen],
    [t('feedback.ctxPageUrl'), pageUrl],
    ...(vehicle?.vin ? ([[t('feedback.ctxVin'), vehicle.vin]] as [string, string][]) : []),
    ...(vehicle?.details
      ? ([[t('feedback.ctxVehicle'), vehicle.details]] as [string, string][])
      : []),
    ...(isInternal && vehicle?.program
      ? ([[t('feedback.ctxProgram'), vehicle.program]] as [string, string][])
      : []),
    ...(isInternal && vehicle?.release
      ? ([[t('feedback.ctxRelease'), vehicle.release]] as [string, string][])
      : []),
  ];

  // Both halves come from the shared context contract: it keeps the program code out of the name
  // fallback for dealers, and it drops the VIN once the description is the VIN itself, which would
  // otherwise print the VIN twice.
  const attachedVehicle = vehicle ? vehicleContext(vehicle, isInternal) : null;
  const attachedValue = attachedVehicle
    ? `${attachedVehicle.primary}${attachedVehicle.vin ? ` \u00b7 ${t('feedback.ctxVin')} ${attachedVehicle.vin}` : ''}`
    : t('feedback.attachedScreen', { screen });

  // Feedback type chips: below contact for internal users, above description for dealers.
  const typeBlock = (
    <>
      <div className="fb-sec">{t('feedback.whatKind')}</div>
      <div className="fb-types">
        {TYPES.map(([val, labelKey, icon]) => (
          <button
            key={val}
            className={`fb-type${type === val ? ' on' : ''}`}
            onClick={() => setType(val)}
          >
            <Icon name={icon} size={16} />
            {t(labelKey)}
          </button>
        ))}
      </div>
    </>
  );

  // Description + attachments (screenshot preview and any extra files).
  const bodyBlock = (
    <>
      <label className="fb-f">
        <span className="fl">
          {t('feedback.descriptionLabel')}
          <span className="req"> *</span>
        </span>
        <textarea
          className={`fb-ta fb-ta-tall${text && trimmed < MIN_CHARS ? ' fb-ta-err' : ''}`}
          maxLength={1000}
          placeholder={t('feedback.descriptionPlaceholder')}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="fb-ta-foot">
          <span className={`fb-help${text && trimmed < MIN_CHARS ? ' fb-help-err' : ''}`}>
            {descHint}
          </span>
          <span className={`fb-count${text.length > 900 ? ' fb-count-warn' : ''}`}>
            {text.length} / 1000
          </span>
        </div>
      </label>

      <div className="fb-sec">{t('feedback.attachments')}</div>
      {shot ? (
        <div className="fb-shot">
          {shotUrl ? (
            <button
              type="button"
              className="fb-shot-thumb"
              onClick={() => window.open(shotUrl, '_blank', 'noopener')}
              aria-label={t('feedback.viewScreenshot')}
              title={t('feedback.viewScreenshot')}
            >
              <img className="fb-shot-img" src={shotUrl} alt={t('feedback.screenshotAlt')} />
              <span className="fb-shot-zoom">
                <Icon name="search" size={14} />
              </span>
            </button>
          ) : (
            <div className="fb-shot-thumb fb-shot-thumb-idle">
              <span className="fb-shot-loading">
                <Icon name="scan" size={18} />
              </span>
            </div>
          )}
          <div className="fb-shot-meta">
            <div className="fb-shot-name">{shotFile?.name ?? shotName}</div>
            <div className="fb-shot-sub">
              {capturing ? t('feedback.capturing') : t('feedback.screenshotAuto')}
            </div>
          </div>
          <button
            className="fb-shot-x"
            onClick={removeShot}
            aria-label={t('feedback.removeScreenshot')}
            title={t('feedback.removeScreenshot')}
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      ) : (
        <button className="fb-attach-add" onClick={recaptureShot}>
          <Icon name="scan" size={16} />
          {t('feedback.captureScreenshot')}
        </button>
      )}

      {files.length > 0 && (
        <ul className="fb-files">
          {files.map((f, i) => (
            <li className="fb-file" key={`${f.name}-${i}`}>
              <button
                type="button"
                className="fb-file-view"
                onClick={() => viewFile(f)}
                title={t('feedback.viewFile')}
              >
                <Icon name="scan" size={14} />
                <span className="fb-file-name">{f.name}</span>
              </button>
              <button
                className="fb-file-x"
                onClick={() => removeFile(i)}
                aria-label={t('feedback.removeFile', { name: f.name })}
                title={t('feedback.removeFile', { name: f.name })}
              >
                <Icon name="close" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        className="fb-attach-more"
        onClick={() => fileInput.current?.click()}
        disabled={files.length >= MAX_FILES}
      >
        <Icon name="plus" size={16} />
        {t('feedback.addFiles')}
      </button>
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        accept="image/png,image/jpeg,application/pdf"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          const oversized = picked.filter((f) => !withinSizeLimit(f));
          const accepted = picked.filter(withinSizeLimit);
          setError(
            oversized.length
              ? t('feedback.fileTooLarge', { names: oversized.map((f) => f.name).join(', ') })
              : null,
          );
          if (accepted.length) {
            // Append (capped) rather than replace, so multiple picks accumulate.
            setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES));
          }
          // Reset so re-selecting the same file still fires onChange.
          e.target.value = '';
        }}
      />
      <span className="fb-help">{t('feedback.attachmentsHelp')}</span>
    </>
  );

  const partialCopy = partialKind ? PARTIAL_COPY[partialKind] : null;

  return (
    <>
      <button
        ref={fabRef}
        className="fb-fab"
        data-tour="feedback"
        onPointerDown={startDrag}
        onClick={() => {
          if (moved.current) {
            moved.current = false;
            return;
          }
          openFeedback();
        }}
        title={t('feedback.title')}
        aria-label={t('feedback.title')}
      >
        <Icon name="chat" size={20} />
      </button>

      {open && (
        <Modal onClose={close} labelledBy="fb-title">
          <div className="modal fb-modal">
            <div className="modal-head mh-col">
              <Icon
                name="chat"
                size={22}
                style={{ color: 'var(--color-primary-text)', flex: 'none' }}
              />
              <div className="mh-titles">
                <h2 id="fb-title">{t('feedback.title')}</h2>
                <div className="mh-sub">{t('feedback.modalSubtitle')}</div>
              </div>
              <div className="sp" style={{ flex: 1 }} />
              <button className="iclose" onClick={close} aria-label={t('common.close')}>
                <Icon name="close" size={18} />
              </button>
            </div>

            {sent ? (
              <div className="fb-success">
                <span className={partialCopy ? 'fb-ok fb-ok-warn' : 'fb-ok'}>
                  <Icon name={partialCopy ? 'warn' : 'check'} size={26} />
                </span>
                <div className="fb-ok-t">
                  {partialCopy ? t(partialCopy.title) : t('feedback.successTitle')}
                </div>
                <div className="fb-ok-d">
                  {partialCopy
                    ? t(partialCopy.detail)
                    : t('feedback.successDetail', {
                        screenshot: shot && shotFile ? t('feedback.successScreenshot') : '',
                      })}
                </div>
                <button className="btn btn-pri" onClick={close}>
                  {t('feedback.done')}
                </button>
              </div>
            ) : (
              <div className="ov-body fb-form">
                <div className="fb-note">
                  <Icon name="info" size={16} />
                  <span>{t('feedback.formNote')}</span>
                </div>

                <div className="fb-2col">
                  <div className="fb-colL">
                    <div className="fb-sec">{t('feedback.contactDetails')}</div>
                    {identitySettled ? (
                      <div className="pf-grid">
                        {identityField(t('feedback.firstName'), 'firstName')}
                        {identityField(t('feedback.lastName'), 'lastName')}
                        {isDealer && (
                          <>
                            {identityField(t('feedback.phone'), 'phone', {
                              inputMode: 'tel',
                              placeholder: t('feedback.phonePlaceholder'),
                              invalid: phoneHint(identity.phone.value),
                            })}
                            {/* A second way to reach the reporter, worth having on a report someone
                                will follow up on. Seeded from the profile but left editable there
                                and here, since nothing but the user ever supplies these. */}
                            {field(t('profile.alternatePhone'), 'altPhone', {
                              optional: true,
                              inputMode: 'tel',
                              invalid: phoneHint(form.altPhone),
                            })}
                          </>
                        )}
                        {identityField(t('feedback.email'), 'email', {
                          inputMode: 'email',
                          invalid: emailHint(identity.email.value),
                        })}
                        {isDealer &&
                          field(t('profile.secondaryEmail'), 'altEmail', {
                            optional: true,
                            inputMode: 'email',
                            placeholder: t('profile.secondaryEmailPlaceholder'),
                            invalid: emailHint(form.altEmail),
                          })}
                      </div>
                    ) : (
                      <div className="pf-empty">
                        <InlineLoading label={t('common.loading')} />
                      </div>
                    )}

                    {/* Shown whether or not it needed asking for: the reporter should be able to
                        see the dealership their report will be filed under. Anything already
                        settled is read-only, since it is changed in the profile, not here. */}
                    {isDealer ? (
                      <>
                        <div className="fb-sec">{t('feedback.dealershipDetails')}</div>
                        {addressSettled ? (
                          <>
                            {!address.complete && (
                              <p className="fb-help">{t('feedback.dealershipMissing')}</p>
                            )}
                            <DealershipAddressFields address={address} readOnlyWhereKnown />
                          </>
                        ) : (
                          <div className="pf-empty">
                            <InlineLoading label={t('profile.dealerGdlLoading')} />
                          </div>
                        )}
                      </>
                    ) : (
                      typeBlock
                    )}
                  </div>

                  <div className="fb-colR">
                    {isDealer && typeBlock}
                    {bodyBlock}
                  </div>
                </div>

                <div className="fb-attached">
                  <Icon name="info" size={16} />
                  <div className="fb-att-main">
                    <div className="fb-att-t">{t('feedback.attachedTitle')}</div>
                    <div className="fb-att-v">{attachedValue}</div>
                  </div>
                  <button
                    className="fb-edit"
                    onClick={() => setDetailsOpen((v) => !v)}
                    aria-pressed={detailsOpen}
                  >
                    {detailsOpen ? t('feedback.detailsHide') : t('feedback.detailsShow')}
                  </button>
                </div>

                {detailsOpen && (
                  <div className="fb-details">
                    {ctxRows.map(([k, v]) => (
                      <div className="fb-ctx-full" key={k}>
                        <div className="fb-ctx-k">{k}</div>
                        <div className="fb-ctx-v">{v}</div>
                      </div>
                    ))}
                  </div>
                )}

                {error && <div className="set-error">{error}</div>}
              </div>
            )}

            {!sent && (
              <div className="mecl-foot" style={{ justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={close} disabled={sending}>
                  {t('feedback.cancel')}
                </button>
                <button
                  className={`btn btn-pri${canSend ? '' : ' btn-disabled'}`}
                  disabled={!canSend}
                  onClick={send}
                >
                  <Icon name="arrow" size={16} />
                  {sending ? t('feedback.sending') : t('feedback.title')}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
